import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COMPANY, EXTRAS, FIXED_EXTRAS, PRICE_LEVELS, MATERIAL_TAGS } from "@/data/services";

function eur(n) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0));
}

async function loadLogoDataUrl() {
  try {
    const res = await fetch(COMPANY.logo);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

export async function exportPublicEstimatePDF(data) {
  const {
    serviceCategory, item, quantity, priceLevelId, estimate,
    selectedExtras, selectedFixed,
    clientName, clientPhone, clientZone, propertyType, timing, notes,
  } = data;

  const logoData = await loadLogoDataUrl();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;

  const level = PRICE_LEVELS.find(p => p.id === priceLevelId) || PRICE_LEVELS[1];
  const extrasList = EXTRAS.filter(e => selectedExtras.includes(e.id));
  const fixedList = FIXED_EXTRAS.filter(f => selectedFixed.includes(f.id));

  // ========== CABECERA ==========
  doc.setFillColor(10, 25, 47);
  doc.rect(0, 0, W, 120, 'F');
  doc.setFillColor(0, 75, 135);
  doc.rect(0, 120, W, 4, 'F');

  if (logoData) {
    try { doc.addImage(logoData, 'PNG', M, 18, 84, 84); } catch {}
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(COMPANY.name, M + 100, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(173, 200, 230);
  doc.text(COMPANY.tagline, M + 100, 58);
  doc.text(`${COMPANY.area}  ·  NIF: ${COMPANY.nif}`, M + 100, 72);

  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTACTO', W - M, 30, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 220, 240);
  const contactLines = [
    `Tel/WhatsApp: ${COMPANY.whatsappFormatted}`,
    `Tel: ${COMPANY.phone2Formatted}`,
    COMPANY.email,
    COMPANY.website,
    `@${COMPANY.instagram}`,
  ];
  contactLines.forEach((l, i) => doc.text(l, W - M, 44 + i * 12, { align: 'right' }));

  let y = 150;

  // ========== TÍTULO ==========
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('ESTIMACIÓN ORIENTATIVA DE REFORMA', M, y);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }), M, y + 14);

  y += 36;

  // ========== DATOS DEL CLIENTE ==========
  if (clientName || clientPhone || clientZone) {
    doc.setFillColor(230, 240, 250);
    doc.rect(M, y, W - 2 * M, 64, 'F');
    doc.setDrawColor(0, 75, 135);
    doc.setLineWidth(2);
    doc.line(M, y, M, y + 64);
    doc.setLineWidth(0.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 75, 135);
    doc.text('DATOS DEL CLIENTE', M + 12, y + 16);

    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    const fields = [
      ['Cliente', clientName || '—'],
      ['Teléfono', clientPhone || '—'],
      ['Zona de la obra', clientZone || '—'],
      ['Tipo', propertyType || '—'],
      ['Plazo', timing || '—'],
    ];
    fields.forEach((f, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const xc = M + 12 + col * ((W - 2 * M - 24) / 2);
      const yc = y + 30 + row * 14;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.text(f[0].toUpperCase(), xc, yc);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(9);
      doc.text(String(f[1]), xc + 75, yc);
    });

    y += 80;
  }

  // ========== DETALLE DE LA ESTIMACIÓN ==========
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Detalle de la estimación', M, y);
  y += 8;

  const materialTagLabel = item.material ? MATERIAL_TAGS[item.material]?.label || '' : '';
  const rows = [
    ['Servicio seleccionado', `${serviceCategory} — ${item.name}`],
    ['Unidad', item.unit],
    ['Medición', `${quantity} ${item.unit}`],
    ['Precio base', `${item.price} €/${item.unit}`],
    ['Subtotal', eur(item.price * quantity)],
    ['Nivel seleccionado', `${level.label} (×${level.factor.toFixed(2)})`],
    ['Suplementos aplicados', extrasList.length ? extrasList.map(e => `${e.label} +${Math.round(e.pct*100)}%`).join(', ') : 'Ninguno'],
    ['Conceptos adicionales', fixedList.length ? fixedList.map(f => `${f.label} (${f.amount}€)`).join(', ') : 'Ninguno'],
    ['Total estimado', eur(estimate.total)],
    ['Material', materialTagLabel || '—'],
    ['IVA', 'No incluido en la estimación (10% aplicable a reforma en vivienda habitual)'],
  ];

  autoTable(doc, {
    startY: y + 6,
    body: rows,
    bodyStyles: { fontSize: 9, textColor: [30, 41, 59], cellPadding: 6 },
    columnStyles: {
      0: { cellWidth: 150, fontStyle: 'bold', textColor: [0, 75, 135] },
      1: { cellWidth: 'auto' },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: M, right: M },
    theme: 'plain',
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`${COMPANY.name}  ·  NIF ${COMPANY.nif}  ·  ${COMPANY.website}`, W / 2, H - 24, { align: 'center' });
      doc.text(`Documento generado el ${new Date().toLocaleDateString('es-ES')}  ·  Estimación orientativa, no presupuesto cerrado`, W / 2, H - 14, { align: 'center' });
    },
  });

  let afterY = doc.lastAutoTable.finalY + 18;

  // ========== INCLUYE / NO INCLUYE ==========
  if (item.incluye?.length || item.noIncluye?.length) {
    if (afterY > H - 200) { doc.addPage(); afterY = 60; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text('¿Qué incluye y qué no incluye?', M, afterY);
    afterY += 14;
    if (item.incluye?.length) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(5, 150, 105);
      doc.text('Incluye:', M, afterY);
      afterY += 12;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      item.incluye.forEach(t => {
        doc.text(`•  ${t}`, M + 8, afterY);
        afterY += 11;
      });
      afterY += 4;
    }
    if (item.noIncluye?.length) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(202, 138, 4);
      doc.text('No incluye:', M, afterY);
      afterY += 12;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      item.noIncluye.forEach(t => {
        doc.text(`•  ${t}`, M + 8, afterY);
        afterY += 11;
      });
      afterY += 4;
    }
  }

  // ========== PRECIO ESTIMADO ==========
  if (afterY > H - 200) { doc.addPage(); afterY = 60; }

  doc.setFillColor(0, 75, 135);
  doc.rect(M, afterY, W - 2 * M, 60, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('PRECIO ESTIMADO ORIENTATIVO', M + 14, afterY + 22);
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.text(`desde ${eur(estimate.total)}`, M + 14, afterY + 48);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 200, 230);
  doc.text('IVA no incluido · Pendiente de visita técnica', W - M - 14, afterY + 48, { align: 'right' });

  afterY += 80;

  // ========== OBSERVACIONES CLIENTE ==========
  if (notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text('Observaciones del cliente', M, afterY);
    afterY += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const nlines = doc.splitTextToSize(notes, W - 2 * M);
    doc.text(nlines, M, afterY);
    afterY += nlines.length * 11 + 12;
  }

  // ========== AVISOS LEGALES ==========
  if (afterY > H - 200) { doc.addPage(); afterY = 60; }

  doc.setFillColor(254, 252, 232);
  doc.setDrawColor(252, 211, 77);
  doc.setLineWidth(0.5);
  doc.rect(M, afterY, W - 2 * M, 90, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(146, 64, 14);
  doc.text('Avisos importantes', M + 12, afterY + 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120, 53, 15);
  const avisos = [
    'Esta estimación es orientativa. El presupuesto definitivo se confirmará tras visita técnica, medición real y revisión del estado de la obra.',
    `Los precios mostrados no constituyen una oferta contractual cerrada. El presupuesto final será válido únicamente tras revisión técnica y aceptación por escrito de ${COMPANY.name}.`,
    'El precio puede variar según: estado del soporte, accesos, altura, materiales, urgencia, vivienda ocupada, remates especiales, licencias o tasas.',
  ];
  let yy = afterY + 30;
  avisos.forEach(a => {
    const lines = doc.splitTextToSize(a, W - 2 * M - 24);
    doc.text(lines, M + 12, yy);
    yy += lines.length * 10 + 3;
  });

  afterY += 110;

  // ========== CTA WHATSAPP ==========
  if (afterY > H - 70) { doc.addPage(); afterY = 60; }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Solicita tu visita técnica:', M, afterY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(0, 75, 135);
  doc.text(`WhatsApp: ${COMPANY.whatsappFormatted}  ·  https://wa.me/${COMPANY.whatsappIntl}`, M, afterY + 16);

  const fileName = `Estimacion_${COMPANY.shortName.replace(/\s+/g, '_')}_${(clientName || 'cliente').replace(/\s+/g, '_')}.pdf`;
  doc.save(fileName);
}
